import { Css } from "@homebound/beam";
import { ProductOfferingTlivChangeSummaryFragment } from "src/routes/developments/product-offerings/version-history/ProductOfferingVersionHistory.generated";
import { AggregateMemberVersionChangeType } from "src/routes/libraries/design-catalog/design-package-configurator/components/DesignUpdatesAvailableButton";
import { GeneralTlivChangesFragment } from "src/routes/libraries/versioning/GeneralTlivChanges.generated";
import { renderAccordion } from "src/routes/libraries/versioning/VersionHistoryUtils";
import { count } from "src/utils";

export type TlivChangesProps = { tlivChangeSummary: ProductOfferingTlivChangeSummaryFragment[] };

// General takeoff line item changes - includes added, removed, and updated takeoff line items.
// Reads the pre-aggregated `tlivChangeSummary` (one entry per change type, already grouped by
// effective location) so large offerings don't have to load every `tliVersions` row. See ON-276.
export function GeneralTlivChanges({ tlivChangeSummary }: TlivChangesProps) {
  const byChangeType = tlivChangeSummary.keyBy((s) => s.changeType);

  return (
    <>
      {renderTlivAccordion("Added", byChangeType[AggregateMemberVersionChangeType.ADDED])}
      {renderTlivAccordion("Removed", byChangeType[AggregateMemberVersionChangeType.REMOVED])}
      {renderTlivAccordion("Updated", byChangeType[AggregateMemberVersionChangeType.UPDATED])}
    </>
  );
}

function renderTlivAccordion(title: string, summary: ProductOfferingTlivChangeSummaryFragment | undefined) {
  if (!summary || summary.total === 0) return null;
  return renderAccordion(
    `${title} ${count(summary.total, "takeoff line-item")}`,
    <ul css={Css.lg.m0.$}>
      {summary.byLocation.map(({ location, count: n }) => (
        <li key={location.id} css={Css.md.$}>
          {`${location.name} (${n})`}
        </li>
      ))}
    </ul>,
  );
}

// Legacy raw-`tliVersions` renderer. Still used by the Plan Package version-history page, which
// has not yet migrated to the aggregated `tlivChangeSummary` API. Groups the raw rows by change
// type and effective location client-side. TODO(ON-276 follow-up): migrate PP and delete this.
export type TlivChangesFromVersionsProps = { tliVersions: GeneralTlivChangesFragment[] };

export function GeneralTlivChangesFromVersions({ tliVersions }: TlivChangesFromVersionsProps) {
  const groupedTlivs = tliVersions.groupBy((tliv) => tliv.changeType);
  const newTlivs = groupedTlivs[AggregateMemberVersionChangeType.ADDED] ?? [];
  const removedTlivs = groupedTlivs[AggregateMemberVersionChangeType.REMOVED] ?? [];
  const updatedTlivs = groupedTlivs[AggregateMemberVersionChangeType.UPDATED] ?? [];

  if (newTlivs.isEmpty && removedTlivs.isEmpty && updatedTlivs.isEmpty) return null;

  return (
    <>
      {renderTlivFromVersionsAccordion("Added", newTlivs)}
      {renderTlivFromVersionsAccordion("Removed", removedTlivs)}
      {renderTlivFromVersionsAccordion("Updated", updatedTlivs)}
    </>
  );
}

function renderTlivFromVersionsAccordion(title: string, tlivs: GeneralTlivChangesFragment[]) {
  if (tlivs.isEmpty) return null;
  return renderAccordion(
    `${title} ${count(tlivs, "takeoff line-item")}`,
    <ul css={Css.lg.m0.$}>
      {tlivs
        .groupByObject((tliv) => tliv.location.effectiveLocation)
        .map(([loc, tlivs]) => (
          <li key={loc?.id} css={Css.md.$}>
            {`${loc?.name} (${tlivs.length})`}
          </li>
        ))}
    </ul>,
  );
}
